'use client';
import React from 'react';
import { Search, Calendar, Users, MapPin } from 'lucide-react';

interface HeroSearchProps {
  checkIn: string;
  checkOut: string;
  guests: string;
  onCheckInChange: (val: string) => void;
  onCheckOutChange: (val: string) => void;
  onGuestsChange: (val: string) => void;
}

export default function HeroSearch({
  checkIn,
  checkOut,
  guests,
  onCheckInChange,
  onCheckOutChange,
  onGuestsChange,
}: HeroSearchProps) {
  const handleSearch = () => {
    const section = document.getElementById('apartments-section');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative overflow-hidden">
      {/* Background gradient */}
      <div className="hero-gradient min-h-[600px] lg:min-h-[680px] flex items-center relative">
        {/* Decorative blobs */}
        <div className="absolute top-10 right-10 w-72 h-72 blob-teal opacity-40 pointer-events-none" />
        <div className="absolute bottom-0 left-20 w-64 h-64 blob-coral opacity-30 pointer-events-none" />

        {/* Floating emoji decorations */}
        <div className="absolute top-16 left-[8%] text-4xl opacity-70 select-none">🌴</div>
        <div className="absolute top-24 right-[12%] text-3xl opacity-60 select-none">🌊</div>
        <div className="absolute bottom-24 left-[15%] text-3xl opacity-50 select-none">☀️</div>
        <div className="absolute top-32 right-[30%] text-2xl opacity-40 select-none">🐚</div>

        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 w-full py-20 relative z-10">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white text-sm font-600 px-4 py-2 rounded-full mb-6">
              <MapPin size={14} />
              Ksamil, Albania · 4 Apartments Available
            </div>

            {/* Headline */}
            <h1 className="text-hero-xl font-800 text-white leading-tight mb-4 text-balance">
              Your Dream Vacation<br />
              <span className="text-accent">Starts Here</span>
            </h1>
            <p className="text-lg text-white/85 mb-10 max-w-xl leading-relaxed">
              Handpicked apartments within easy walking distance to several beachs. Instant booking, no hidden fees, memories that last forever.
            </p>

            {/* Search Widget */}
            <div className="bg-white rounded-2xl shadow-2xl p-4 lg:p-5 flex flex-col lg:flex-row gap-3 lg:gap-2 max-w-2xl">
              {/* Check-in */}
              <div className="flex-1">
                <label className="block text-[11px] font-700 uppercase tracking-wider text-muted-foreground mb-1.5 px-1">
                  Check-in
                </label>
                <div className="relative">
                  <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    type="date"
                    value={checkIn}
                    onChange={(e) => onCheckInChange(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full pl-9 pr-3 py-2.5 bg-muted rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring border border-transparent focus:border-transparent"
                  />
                </div>
              </div>

              <div className="hidden lg:flex items-end pb-2.5 text-muted-foreground">→</div>

              {/* Check-out */}
              <div className="flex-1">
                <label className="block text-[11px] font-700 uppercase tracking-wider text-muted-foreground mb-1.5 px-1">
                  Check-out
                </label>
                <div className="relative">
                  <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    type="date"
                    value={checkOut}
                    onChange={(e) => onCheckOutChange(e.target.value)}
                    min={checkIn || new Date().toISOString().split('T')[0]}
                    className="w-full pl-9 pr-3 py-2.5 bg-muted rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring border border-transparent focus:border-transparent"
                  />
                </div>
              </div>

              {/* Guests */}
              <div className="w-full lg:w-36">
                <label className="block text-[11px] font-700 uppercase tracking-wider text-muted-foreground mb-1.5 px-1">
                  Guests
                </label>
                <div className="relative">
                  <Users size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <select
                    value={guests}
                    onChange={(e) => onGuestsChange(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 bg-muted rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring border border-transparent appearance-none">
                    {['1', '2', '3', '4', '5', '6', '7', '8'].map((n) => (
                      <option key={`guest-opt-${n}`} value={n}>
                        {n} Guest{n !== '1' ? 's' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Search Button */}
              <div className="flex items-end">
                <button
                  onClick={handleSearch}
                  className="w-full lg:w-auto bg-primary text-white font-700 px-6 py-2.5 rounded-xl flex items-center justify-center gap-2 hover:opacity-90 active:scale-95 transition-all duration-150 shadow-coral">
                  <Search size={16} />
                  Search
                </button>
              </div>
            </div>

            {/* Quick stats */}
            <div className="flex flex-wrap gap-6 mt-8">
              {[
                { value: '4', label: 'Apartments' },
                { value: '4.9★', label: 'Avg Rating' },
                { value: '200+', label: 'Happy Guests' },
                { value: '0%', label: 'Hidden Fees' },
              ].map((stat) => (
                <div key={`hero-stat-${stat.label}`} className="text-white/90">
                  <span className="text-xl font-800">{stat.value}</span>
                  <span className="text-sm ml-1.5 opacity-70">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Wave divider */}
      <div className="bg-background">
        <svg viewBox="0 0 1440 60" className="w-full -mt-1 fill-background" preserveAspectRatio="none" style={{ height: '60px' }}>
          <path d="M0,0 C360,60 1080,60 1440,0 L1440,60 L0,60 Z" />
        </svg>
      </div>
    </section>
  );
}